import React, { useMemo, useState } from 'react';
import { DrawerDetalhesTerminal } from './DrawerDetalhesTerminal';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Button,
  Checkbox,
  Filter,
  FilterDropdown,
  FilterDropdownItem,
  Icon,
  IconButton,
  Modal,
  PageHeading,
  Pills,
  Search,
  Select,
  Table,
  Tabs,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  Export,
  LinkBreak,
  LinkSimple,
  X,
  CheckCircleIcon,
  WarningIcon,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { DrawerOperacoesEmLote } from './DrawerOperacoesEmLote';
import type { OperationType } from './DrawerOperacoesEmLote';
import { isTransactionActive } from '../../_shared/terminalUtils';
import { ExportModal } from '../../_shared/ExportModal';
import './POS.css';
import { INITIAL_TERMINALS } from './pos-mock-data';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TerminalStatus =
  | 'Teste'
  | 'Estoque Barte'
  | 'Estoque Intermediador'
  | 'Alocada Lojista'
  | 'Manutenção'
  | 'Desativada';

export interface VinculationEntry {
  payfac: string;
  payfacId: string;
  seller: string;
  sellerId: string;
  lastTransactionDate: string;
  vinculationDate: string;
  devinculationDate: string;
}

export type TerminalOwner = 'Barte' | 'Própria';

export interface Terminal {
  id: string;
  serialNumber: string;
  status: TerminalStatus;
  owner: TerminalOwner;
  payfac: string;
  payfacId: string;
  seller: string;
  lastTransaction: string;
  sellerId: string;
  terminalId: string;
  posVersion: string;
  vinculationDate: string;
  registrationDate: string;
  lastTestDate?: string;
  failureReason?: string;
  vinculationHistory?: VinculationEntry[];
  vinculationStartDate?: string;
}

// ─── Tabs config ──────────────────────────────────────────────────────────────

type TabKey = 'todos' | TerminalStatus;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'todos',           label: 'Todos' },
  { key: 'Teste',           label: 'Teste' },
  { key: 'Estoque Barte',   label: 'Estoque Barte' },
  { key: 'Estoque Intermediador',  label: 'Estoque Intermediador' },
  { key: 'Alocada Lojista',        label: 'Alocada Lojista' },
  { key: 'Manutenção',      label: 'Manutenção' },
  { key: 'Desativada',      label: 'Desativada' },
];

// ─── Bulk action logic ────────────────────────────────────────────────────────

function deriveBulkStatus(selected: Terminal[]): TerminalStatus | 'mixed' | null {
  if (!selected.length) return null;
  const statuses = Array.from(new Set(selected.map((t) => t.status)));
  return statuses.length === 1 ? statuses[0] : 'mixed';
}

// ─── Table columns ────────────────────────────────────────────────────────────

type PillsStateValue = 'success' | 'info' | 'warning' | 'error' | 'default' | 'accent' | 'alert';

export const STATUS_PILL: Record<TerminalStatus, PillsStateValue> = {
  'Teste':           'accent',
  'Estoque Barte':   'info',
  'Estoque Intermediador':  'info',
  'Alocada Lojista':        'success',
  'Manutenção':      'warning',
  'Desativada':      'default',
};

export const STATUS_TOOLTIP: Record<TerminalStatus, string> = {
  'Teste':           'Terminal recém-cadastrado, vinculado ao ambiente de testes Barte. Aguardando aprovação no checklist.',
  'Estoque Barte':   'Aprovado nos testes. Disponível para ser vinculado a um Intermediador.',
  'Estoque Intermediador':  'Vinculado ao Intermediador e aguardando alocação para um Lojista.',
  'Alocada Lojista':        'Terminal ativo e vinculado a um Intermediador e Lojista. Pronto para transações.',
  'Manutenção':      'Falha detectada. Terminal fora de operação aguardando reparo.',
  'Desativada':      'Terminal desativado e fora de operação.',
};

export const OWNER_TOOLTIP: Record<TerminalOwner, string> = {
  'Barte':   'Máquina da Barte',
  'Própria': 'Máquina do cliente',
};


const BASE_COLUMNS: ColumnDefinition<Terminal>[] = [
  {
    key: 'id',
    title: 'POS',
    dataIndex: 'id',
    width: 170,
    render: (_value, record) => (
      <div className="pos-cell">
        <Text variant="label-sm">{record.id}</Text>
        <Text variant="body-xs" className="pos-serial">{record.serialNumber}</Text>
      </div>
    ),
  },
  {
    key: 'owner',
    title: 'Proprietário',
    dataIndex: 'owner',
    width: 130,
    render: (value) => (
      <div className="status-tooltip" data-tooltip={OWNER_TOOLTIP[value as TerminalOwner]}>
        <Pills
          label={value as string}
          state="default"
          variant={value === 'Barte' ? 'fill' : 'light'}
          size="sm"
        />
      </div>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
    width: 180,
    render: (value) => (
      <div className="status-tooltip" data-tooltip={STATUS_TOOLTIP[value as TerminalStatus]}>
        <Pills
          label={value as string}
          state={STATUS_PILL[value as TerminalStatus]}
          size="sm"
        />
      </div>
    ),
  },
  {
    key: 'payfac',
    title: 'Intermediador',
    dataIndex: 'payfac',
    width: 170,
    render: (_value, record) => (
      <div className="pos-cell">
        <Text variant="label-sm">{record.payfac}</Text>
        <Text variant="body-xs" className="pos-serial">{record.payfacId}</Text>
      </div>
    ),
  },
  {
    key: 'seller',
    title: 'Lojista',
    dataIndex: 'seller',
    width: 210,
    render: (_value, record) => (
      <div className="pos-cell">
        <Text variant="label-sm">{record.seller}</Text>
        <Text variant="body-xs" className="pos-serial">{record.sellerId}</Text>
      </div>
    ),
  },
  {
    key: 'lastTransaction',
    title: 'Última transação',
    dataIndex: 'lastTransaction',
    width: 165,
  },
  {
    key: 'vinculationDate',
    title: 'Data de vinculação',
    dataIndex: 'vinculationDate',
    width: 155,
  },
  {
    key: 'chipNumber',
    title: 'Número do chip',
    dataIndex: 'id',
    width: 130,
    render: () => '—',
  },
  {
    key: 'terminalId',
    title: 'ID Phoebus POS',
    dataIndex: 'terminalId',
    width: 140,
  },
  {
    key: 'sellerId',
    title: 'ID Lojista Phoebus',
    dataIndex: 'sellerId',
    width: 155,
  },
  {
    key: 'posVersion',
    title: 'Versão do POS',
    dataIndex: 'posVersion',
    width: 120,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function POS() {
  const [terminals, setTerminals] = useState<Terminal[]>(INITIAL_TERMINALS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('todos');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);

  // Config do drawer de operações em lote
  const [drawerConfig, setDrawerConfig] = useState<{
    initialTerminals?: string[];
    initialOperation?: OperationType;
    initialStep?: number;
    initialPayfac?: string;
    autoProcess?: boolean;
    desvincularChoice?: boolean;
  }>({});

  // Insere a coluna "Status transacional" após "Status" — só preenche na view Alocada Lojista
  const columns = useMemo((): ColumnDefinition<Terminal>[] => {
    const statusTransacional: ColumnDefinition<Terminal> = {
      key: 'statusTransacional',
      title: 'Status transacional',
      dataIndex: 'lastTransaction',
      width: 175,
      render: (_value, record) => {
        if (activeTab !== 'Alocada Lojista') return '—';
        const active = isTransactionActive(record.lastTransaction);
        return (
          <Pills
            label={active ? 'Ativa' : 'Inativa'}
            state={active ? 'success' : 'default'}
            size="sm"
          />
        );
      },
    };
    const result = [...BASE_COLUMNS];
    result.splice(3, 0, statusTransacional);
    return result;
  }, [activeTab]);

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<TerminalStatus[]>([]);
  const [activeFilters, setActiveFilters] = useState<TerminalStatus[]>([]);
  const [statusItemOpen, setStatusItemOpen] = useState(true);

  function handleSearchExpand(expanded: boolean) {
    setSearchExpanded(expanded);
    if (!expanded) setSearchQuery('');
  }

  function handleFilterToggle() {
    if (!filterOpen) setPendingFilters(activeFilters);
    setFilterOpen((o) => !o);
  }

  function handleFilterApply() {
    setActiveFilters(pendingFilters);
    setFilterOpen(false);
    if (pendingFilters.length > 0) setActiveTab('todos');
    setPage(1);
  }

  function handleFilterClear() {
    setPendingFilters([]);
    setActiveFilters([]);
    setFilterOpen(false);
    setPage(1);
  }

  function handleFilterClose() {
    setFilterOpen(false);
    setPendingFilters(activeFilters);
  }

  function togglePending(status: TerminalStatus) {
    setPendingFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  }

  // Filtra dados conforme tab ativa + filtros de status + busca em tempo real
  const filteredData = useMemo(() => {
    const byTab = activeTab === 'todos'
      ? terminals
      : terminals.filter((t) => t.status === activeTab);

    const byFilter = activeFilters.length > 0
      ? byTab.filter((t) => activeFilters.includes(t.status))
      : byTab;

    const q = searchQuery.trim().toLowerCase();
    if (!q) return byFilter;

    return byFilter.filter((t) =>
      t.id.toLowerCase().includes(q) ||
      t.payfac.toLowerCase().includes(q) ||
      t.seller.toLowerCase().includes(q) ||
      t.sellerId.toLowerCase().includes(q),
    );
  }, [activeTab, searchQuery, terminals, activeFilters]);

  // Contagem por status para badges das tabs
  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = { todos: terminals.length };
    for (const t of terminals) {
      counts[t.status] = (counts[t.status] ?? 0) + 1;
    }
    return counts;
  }, [terminals]);

  // Slice de dados para a página atual
  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // Modais de exportação, mudança de status e confirmação de ações em lote
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBatchStatusModal, setShowBatchStatusModal] = useState(false);
  const [batchTargetStatus, setBatchTargetStatus] = useState<TerminalStatus | ''>('');
  const [pendingBatchTeste, setPendingBatchTeste] = useState<'sucesso' | 'falha' | null>(null);

  // Bulk: terminais selecionados e status homogêneo
  const selectedTerminals = useMemo(
    () => terminals.filter((t) => selectedRowKeys.includes(t.id)),
    [selectedRowKeys, terminals],
  );
  const homogeneousStatus = useMemo(
    () => deriveBulkStatus(selectedTerminals),
    [selectedTerminals],
  );

  function handleTabChange(key: TabKey) {
    setActiveTab(key);
    setPage(1);
    setSelectedRowKeys([]);
    setSearchQuery('');
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(1);
  }

  function openDrawerVincular() {
    const isEstoqueInter = homogeneousStatus === 'Estoque Intermediador';
    setDrawerConfig({
      initialTerminals: selectedTerminals.map((t) => t.serialNumber),
      initialOperation: 'vincular',
      initialStep: isEstoqueInter ? 3 : 2,
      initialPayfac: isEstoqueInter ? selectedTerminals[0]?.payfacId : undefined,
    });
    setDrawerOpen(true);
  }

  function openDrawerDesvincular() {
    const serials = selectedTerminals.map((t) => t.serialNumber);
    if (homogeneousStatus === 'Estoque Intermediador') {
      // Única saída possível: → Estoque Barte. Processa direto.
      setDrawerConfig({ initialTerminals: serials, initialOperation: 'desvincular', autoProcess: true });
    } else if (homogeneousStatus === 'Alocada Lojista' || homogeneousStatus === 'Manutenção') {
      // Usuário precisa escolher: só lojista (→ Estoque Intermediador) ou ambos (→ Estoque Barte).
      setDrawerConfig({ initialTerminals: serials, initialOperation: 'desvincular', desvincularChoice: true });
    } else {
      setDrawerConfig({ initialTerminals: serials, initialOperation: 'desvincular' });
    }
    setDrawerOpen(true);
  }

  function handleBatchTeste(result: 'sucesso' | 'falha') {
    setPendingBatchTeste(result);
  }

  function confirmBatchTeste() {
    if (!pendingBatchTeste) return;
    const ids = new Set(selectedRowKeys);
    const newStatus: TerminalStatus = pendingBatchTeste === 'sucesso' ? 'Estoque Barte' : 'Manutenção';
    setTerminals((prev) => prev.map((t) => (ids.has(t.id) ? { ...t, status: newStatus } : t)));
    setSelectedRowKeys([]);
    setPendingBatchTeste(null);
  }

  function handleBatchStatusApply() {
    if (!batchTargetStatus) return;
    const ids = new Set(selectedRowKeys);
    const newStatus = batchTargetStatus as TerminalStatus;
    setTerminals((prev) => prev.map((t) => (ids.has(t.id) ? { ...t, status: newStatus } : t)));
    setSelectedRowKeys([]);
    setShowBatchStatusModal(false);
    setBatchTargetStatus('');
  }

  function openDrawerDefault() {
    setDrawerConfig({});
    setDrawerOpen(true);
  }

  function handleRowClick(record: Terminal) {
    setSelectedTerminal(record);
    setDetailDrawerOpen(true);
  }

  function handleStatusChange(
    terminalId: string,
    newStatus: TerminalStatus,
    extra?: { failureReason?: string; lastTestDate?: string },
  ) {
    setTerminals((prev) =>
      prev.map((t) => (t.id === terminalId ? { ...t, status: newStatus, ...extra } : t)),
    );
    setSelectedTerminal((prev) =>
      prev?.id === terminalId ? { ...prev, status: newStatus, ...extra } : prev,
    );
  }

  return (
    <>
    <BackofficeLayout activeItem="terminais">
      <main className="content">
          <div className="content-inner">
          <PageHeading
            title="Gestão de POS"
            primaryActionLabel="Nova operação"
            onPrimaryAction={openDrawerDefault}
          />

          {/* ── Bloco de filtros: tabs + ações — separado da tabela ── */}
          <div className="filter-bar">
            <div className="filter-bar-tabs">
              <Tabs>
                {TABS.map((tab) => (
                  <Tabs.Tab
                    key={tab.key}
                    active={activeTab === tab.key}
                    badge={countByStatus[tab.key] ?? 0}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.label}
                  </Tabs.Tab>
                ))}
              </Tabs>
            </div>

            <div className="filter-bar-actions">
              <Button
                variant="outline"
                size="sm"
                iconLeft={<Icon icon={Export} size={16} weight="regular" />}
                onClick={() => setShowExportModal(true)}
              >
                Exportar
              </Button>
              <Search
                expanded={searchExpanded}
                value={searchQuery}
                onChange={setSearchQuery}
                onExpand={handleSearchExpand}
                placeholder="Busque por POS ID, Nome do Intermediador, CNPJ do Lojista, ID do Lojista ou Nome do Lojista"
              />
              <Filter
                label="Filtro"
                isOpen={filterOpen}
                onToggle={handleFilterToggle}
                hasActiveFilters={activeFilters.length > 0}
              >
                <FilterDropdown
                  title="Filtros"
                  onClear={handleFilterClear}
                  onClose={handleFilterClose}
                  onSubmit={handleFilterApply}
                  submitDisabled={pendingFilters.length === 0}
                >
                  <FilterDropdownItem
                    label="Status"
                    isOpen={statusItemOpen}
                    onToggle={() => setStatusItemOpen((o) => !o)}
                    isFilled={pendingFilters.length > 0}
                    onClear={handleFilterClear}
                  >
                    <div className="filter-status-list">
                      {(['Teste', 'Estoque Barte', 'Estoque Intermediador', 'Alocada Lojista', 'Manutenção', 'Desativada'] as TerminalStatus[]).map((status) => (
                        <label key={status} className="filter-status-option">
                          <Checkbox
                            checked={pendingFilters.includes(status)}
                            onChange={() => togglePending(status)}
                          />
                          <Pills label={status} state={STATUS_PILL[status]} size="sm" />
                        </label>
                      ))}
                    </div>
                  </FilterDropdownItem>
                </FilterDropdown>
              </Filter>
            </div>
          </div>

          {/* ── Card da tabela ──────────────────────────────────────── */}
          <div className="content-table">
            <Table<Terminal>
              data={pagedData}
              columns={columns}
              rowKey={(r) => r.id}
              size="sm"
              emptyText="Nenhum terminal encontrado."
              onRowClick={handleRowClick}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              pagination={{
                currentPage: page,
                totalPages,
                totalItems: filteredData.length,
                pageSize,
                pageSizeOptions: [10, 25, 50],
                onPageChange: setPage,
                onPageSizeChange: handlePageSizeChange,
              }}
            />
          </div>
          </div>
        </main>
    </BackofficeLayout>

      <DrawerOperacoesEmLote
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        {...drawerConfig}
      />

      <DrawerDetalhesTerminal
        terminal={selectedTerminal}
        isOpen={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        onStatusChange={handleStatusChange}
      />

      {/* ── Barra de ações em massa ────────────────────────────────────── */}
      {selectedRowKeys.length > 0 && (
        <div className="bulk-bar">
          <div className="bulk-bar-left">
            <Text variant="label-base" className="bulk-bar-count">
              {selectedRowKeys.length} terminal{selectedRowKeys.length !== 1 ? 'is' : ''} selecionado{selectedRowKeys.length !== 1 ? 's' : ''}
            </Text>
          </div>

          <div className="bulk-bar-actions">
            {homogeneousStatus === 'mixed' && (
              <Text variant="body-sm" className="bulk-bar-warning">
                Selecione terminais com mesmo status para executar uma ação
              </Text>
            )}
            {homogeneousStatus === 'Teste' && (
              <>
                <Button variant="fill" size="sm" iconLeft={<Icon icon={CheckCircleIcon} size={16} weight="regular" />} onClick={() => handleBatchTeste('sucesso')}>
                  Sucesso
                </Button>
                <Button variant="outline" size="sm" iconLeft={<Icon icon={WarningIcon} size={16} weight="regular" />} onClick={() => handleBatchTeste('falha')}>
                  Falha
                </Button>
              </>
            )}
            {homogeneousStatus === 'Estoque Barte' && (
              <Button variant="fill" size="sm" iconLeft={<Icon icon={LinkSimple} size={16} weight="regular" />} onClick={openDrawerVincular}>
                Vincular
              </Button>
            )}
            {homogeneousStatus === 'Estoque Intermediador' && (
              <>
                <Button variant="fill" size="sm" iconLeft={<Icon icon={LinkSimple} size={16} weight="regular" />} onClick={openDrawerVincular}>
                  Vincular
                </Button>
                <Button variant="outline" size="sm" iconLeft={<Icon icon={LinkBreak} size={16} weight="regular" />} onClick={openDrawerDesvincular}>
                  Desvincular
                </Button>
              </>
            )}
            {homogeneousStatus === 'Alocada Lojista' && (
              <Button variant="outline" size="sm" iconLeft={<Icon icon={LinkBreak} size={16} weight="regular" />} onClick={openDrawerDesvincular}>
                Desvincular
              </Button>
            )}
            {homogeneousStatus === 'Manutenção' && (
              <>
                <Button variant="fill" size="sm" iconLeft={<Icon icon={ArrowsClockwise} size={16} weight="regular" />} onClick={() => setShowBatchStatusModal(true)}>
                  Mudar status
                </Button>
                <Button variant="outline" size="sm" iconLeft={<Icon icon={LinkBreak} size={16} weight="regular" />} onClick={openDrawerDesvincular}>
                  Desvincular
                </Button>
              </>
            )}
          </div>

          <div className="bulk-bar-right">
            <Button
              variant="outline"
              size="sm"
              className="bulk-bar-cancel"
              onClick={() => setSelectedRowKeys([])}
            >
              Cancelar
            </Button>
            <IconButton
              variant="outline"
              size="sm"
              aria-label="Limpar seleção"
              className="bulk-bar-close"
              onClick={() => setSelectedRowKeys([])}
            >
              <Icon icon={X} size={16} weight="bold" />
            </IconButton>
          </div>
        </div>
      )}

      {/* ── Modal: exportar ───────────────────────────────────────────── */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        totalCount={filteredData.length}
        selectedCount={selectedRowKeys.length}
      />

      {/* ── Modal: confirmar Sucesso / Falha em lote ──────────────────── */}
      <Modal
        isOpen={pendingBatchTeste !== null}
        title={pendingBatchTeste === 'sucesso' ? 'Confirmar aprovação em lote' : 'Confirmar reprovação em lote'}
        onClose={() => setPendingBatchTeste(null)}
        size="sm"
        footer={{
          primaryButton: {
            children: 'Confirmar',
            onClick: confirmBatchTeste,
          },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: () => setPendingBatchTeste(null),
          },
        }}
      >
        <Text variant="body-sm">
          {pendingBatchTeste === 'sucesso'
            ? <>Todos os <strong>{selectedRowKeys.length}</strong> terminais selecionados serão movidos para <strong>Estoque Barte</strong>. Tem certeza?</>
            : <>Todos os <strong>{selectedRowKeys.length}</strong> terminais selecionados serão movidos para <strong>Manutenção</strong>. Tem certeza?</>
          }
        </Text>
      </Modal>

      {/* ── Modal: mudar status em lote (Manutenção) ──────────────────── */}
      <Modal
        isOpen={showBatchStatusModal}
        title="Mudar status em massa"
        onClose={() => { setShowBatchStatusModal(false); setBatchTargetStatus(''); }}
        size="sm"
        footer={{
          primaryButton: {
            children: 'Confirmar',
            onClick: handleBatchStatusApply,
            disabled: !batchTargetStatus,
          },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: () => { setShowBatchStatusModal(false); setBatchTargetStatus(''); },
          },
        }}
      >
        <Text variant="body-sm" style={{ marginBottom: 16 }}>
          Selecione o novo status para os <strong>{selectedRowKeys.length}</strong> terminais em Manutenção.
        </Text>
        <Select
          placeholder="Selecione o novo status"
          value={batchTargetStatus}
          onChange={(val) => setBatchTargetStatus(val as TerminalStatus)}
          options={[
            { value: 'Estoque Barte', label: 'Estoque Barte — terminal reparado' },
            { value: 'Desativada',    label: 'Desativada — terminal fora de operação' },
          ]}
        />
      </Modal>
    </>
  );
}
